// ファイルアップロードのテンプレート
import React, { useState, memo } from 'react';
import { Form, Col, Row, InputGroup, Button } from 'react-bootstrap';
import axios from '../axios';
import Compressor from 'compressorjs';
import {
  pdf_icon,
  compressor_option,
  file_type_error,
  file_upload_error,
  file_upload_clamp_config,
  download_wait_time,
} from '../Const';
import { FileUpload as FileUploadType } from '../types/FileUpload';
import FileSelect from '../molecules/FileSelect';
import RowTemplate from '../atoms/RowTemplate';
import ModalTemplate from '../molecules/ModalTemplate';
import ValidMessage from '../atoms/ValidMessage';
import Canvas from '../molecules/Canvas';
import { isBrowserIOSSafari, isBrowserIpadSafari } from '../BrowserJudgment';
import { releaseBrowzerBack } from '../BrowzerBack';
import LoadingModal from '../molecules/LoadingModal';
import Messages from '../molecules/Messages';

interface Props {
  files_upload: FileUploadType[];
  setFilesUpload: React.Dispatch<React.SetStateAction<FileUploadType[]>>;
  block_disabled?: boolean;
  is_staff: boolean;
  fm_id?: number;
  ident_lv1_cd: string;
  ident_lv2_cd: string;
  ident_lv3_cd: string;
  reception_id: number;
  reception_cd: string;
  reception_sub_cd: string;
  upload_token: string;
  need?: boolean;
  onUpload?: (fileName: string) => void;
  onCancel?: (fileName: string) => void;
}
const FileUpload: React.FC<Props> = ({
  files_upload,
  setFilesUpload,
  block_disabled,
  is_staff,
  fm_id,
  ident_lv1_cd,
  ident_lv2_cd,
  ident_lv3_cd,
  reception_id,
  reception_cd,
  reception_sub_cd,
  upload_token,
  need,
  onUpload,
  onCancel,
}) => {
  const [is_processing, setIsProcessing] = useState(false);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);

  /**
   * Postするformデータを作成
   * @param file_upload ファイルアップロード
   * @param files_upload_info ファイルアップロード情報
   */
  const createFormData = (file_upload: FileUploadType) => {
    const form = new FormData();
    const shelf_cd = file_upload.shelf_cd;
    form.append('file', file_upload.file, file_upload.file_label);
    form.append('file_path', file_upload.file_upload_name);
    form.append('document_cd', file_upload.default_value);
    form.append('ident_lv1_cd', ident_lv1_cd);
    form.append('ident_lv2_cd', ident_lv2_cd);
    form.append('ident_lv3_cd', ident_lv3_cd);
    form.append('reception_id', reception_id.toString());
    form.append('reception_cd', reception_cd);
    form.append('reception_sub_cd', reception_sub_cd);
    form.append('shelf_cd', shelf_cd);
    form.append('fm_id', file_upload.fm_id ? file_upload.fm_id.toString() : '');
    form.append('upload_token', upload_token);
    return form;
  };

  /**
   * バリデーションメッセージの作成
   * @param errors エラーメッセージ
   * @param file_upload ファイルアップロード
   * @param can_file_clear 読み込みファイルの削除フラグ
   */
  const makeValidMessage = (errors: any, file_upload: FileUploadType, can_file_clear: boolean) => {
    const result: { [key: string]: string } = {};
    Object.keys(errors).forEach((key) => {
      result[key] = errors[key].join('');
    });

    setFilesUpload((prev) =>
      prev.map((value) =>
        value.name === file_upload.name
          ? {
              ...file_upload,
              preview_file_src: can_file_clear ? '' : file_upload.preview_file_src,
              file_label: can_file_clear ? '' : file_upload.file_label,
              file: can_file_clear ? new Blob() : file_upload.file,
              is_loading: false,
              loading_msg: '',
              valid_messages: result,
            }
          : value
      )
    );
  };

  /**
   * ファイルアップロードのイベントセレクタ
   * @param file_upload ファイルアップロード
   * @param upload_image アップロード対象画像ファイル
   */
  const handleUpload = async (file_upload: FileUploadType, upload_image: Blob) => {
    //アップロード対象の画像ファイルがない場合は処理を行わない
    if (!upload_image) {
      return;
    }

    try {
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                is_loading: true,
                loading_msg: '少々お待ちください...',
                valid_messages: {},
              }
            : value
        )
      );

      const edit_file_upload = editFileUpload(file_upload, files_upload, upload_image);

      const response = await axios.post('/api/file', createFormData(edit_file_upload));

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                id: response.data.id,
                is_loading: false,
                loading_msg: response.data.summary,
                is_uploaded: true,
                file_upload_date: response.data.file_upload_date,
                file_upload_name: response.data.file_name,
              }
            : value
        )
      );

      onUpload?.(response.data.file_name);
    } catch (error) {
      // バリデーション検知時
      if (
        error.response.status !== undefined &&
        error.response.status !== null &&
        error.response.status === 422
      ) {
        makeValidMessage(error.response.data.errors, file_upload, false);
      } else {
        // ローディング画像非表示
        setFilesUpload((prev) =>
          prev.map((value) =>
            value.name === file_upload.name
              ? {
                  ...file_upload,
                  is_loading: false,
                  loading_msg: 'ファイルアップロードに失敗しました',
                }
              : value
          )
        );
        // ログ出力
        console.error(error);
      }
    } finally {
      // 処理中フラグを無効にする。
      setIsProcessing(false);
    }
  };

  /**
   * アップロード追加ボタン押下のイベントハンドラ
   * @param file_upload ファイルアップロード
   */
  const handleAdd = (file_upload: FileUploadType) => {
    // 処理中フラグを有効にする。
    setIsProcessing(true);

    // 現在の配列から、対象の棚種別（shelf_cd）が一致する最後の要素のインデックスを取得する
    const target_index = files_upload
      .map((item) => item.shelf_cd)
      .lastIndexOf(file_upload.shelf_cd);

    // 追加対象となる新しいファイルオブジェクトを生成する（nameは後述の再採番で設定するため初期値は0）
    const new_file_upload: FileUploadType = {
      name: 0,
      title: file_upload.title,
      default_value: file_upload.source[0].key ?? '',
      shelf_cd: file_upload.shelf_cd,
      fm_id: file_upload.fm_id,
      source: file_upload.source,
      preview_file_src: '',
      file_label: '',
      is_loading: false,
      loading_msg: '',
      is_add: false,
      is_uploaded: false,
      file_upload_date: '',
      confirm_date: '',
      confirm_user: '',
      file: new Blob(),
      valid_messages: {},
      is_modal_show: false,
      file_upload_name: '',
      is_pdf: false,
    };

    // 現在の配列をコピーし、対象グループの最終要素の直後に新規要素を挿入する
    const temp_files_upload = [...files_upload];
    temp_files_upload.splice(target_index + 1, 0, new_file_upload);

    // BEの仕様（name = index）に合わせるため、配列全体のname属性をインデックス値で再採番（同期）する
    const final_files_upload = temp_files_upload.map((item, index) => ({
      ...item,
      name: index, // 挿入後の新しい並び順に基づいてインデックスを再割り当て
    }));

    // ステートを更新し
    setFilesUpload(final_files_upload);

    // 処理中フラグを無効にする。
    setIsProcessing(false);
  };

  /**
   * 削除ボタン押下時のイベントハンドラ
   * @param file_upload ファイルアップロード
   */
  const handleDelete = (file_upload: FileUploadType) => {
    // 処理中フラグを有効にする。
    setIsProcessing(true);

    setFilesUpload((prev) => prev.filter((value) => value.name !== file_upload.name));

    // 処理中フラグを削除にする。
    setIsProcessing(false);
  };

  /**
   * ファイル選択時のイベントハンドラ
   */
  const handleFileSelect = (
    file_upload: FileUploadType,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // event.persist();
    const event_value = event.target.value;
    setFilesUpload((prev) =>
      prev.map((value) =>
        value.name === file_upload.name ? { ...file_upload, default_value: event_value } : value
      )
    );
  };

  /**
   * ファイルインプットのイベントハンドラ
   * @param file_upload ファイルアップロード
   * @param event イベント
   */
  const handleFileInput = (
    file_upload: FileUploadType,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files === null) {
      return;
    }
    const file = event.target.files[0];
    // ファイルの指定がない場合は処理しない
    if (file === null) {
      return;
    }

    // 処理中フラグを有効にする。
    setIsProcessing(true);

    if (file.type.match('image.*') || file.type.match('.*pdf')) {
      //画像ファイルはそのまま,PDFファイルは代用アイコンを画面表示する。
      fileRender(file_upload, file, file.name);
    } else if (file.name.toLowerCase().match(/[.]heic$/)) {
      // heicファイルはJPEGに変換する
      convertHeicToWebImage(file_upload, file);
    } else {
      //画像,pdf以外のファイルの場合はバリデーションメッセージを表示する。
      makeValidMessage(file_type_error, file_upload, true);
    }
    // 処理中フラグを無効にする。
    setIsProcessing(false);
  };

  /**
   * ファイルダウンロード
   * @param file_download ダウンロードするファイル
   */
  const handlefileDownLoad = (file_download: string) => {
    //処理中の場合は処理を中断する。
    if (is_processing) {
      return false;
    }

    //IOS端末かつsafariの場合、ブラウザバック禁止処理を解除してダウンロードしたファイルを表示後に元画面に戻れるようにする。
    if (isBrowserIOSSafari() || isBrowserIpadSafari()) {
      releaseBrowzerBack();
    }

    try {
      // ローディング画面を表示する。
      setIsLoading(true);
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      //ファイルをダウンロードする。
      const url = `${process.env.MIX_MOCK_URL}/api/file/download/${file_download}`;
      const self_download_link = document.createElement('a');
      document.body.appendChild(self_download_link);
      self_download_link.download = `${file_download}`;
      self_download_link.href = url;
      self_download_link.click();
      self_download_link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      // 処理中フラグを無効にする。
      setIsProcessing(false);
      // ローディング停止を遅らせる
      setTimeout(() => {
        setIsLoading(false);
      }, download_wait_time);
    }
  };

  const compressorImage = (file_upload: FileUploadType) => {
    const file = file_upload.file;
    if (!file) {
      handleConfirmTmp(file_upload);
    }
    // pdfファイルの場合圧縮を行わずにアップロードを行う
    else if (
      file.type === 'application/pdf' &&
      file_upload.is_uploaded !== null &&
      file_upload.is_uploaded
    ) {
      handleConfirmTmp(file_upload);
    } else if (file.type === 'application/pdf' && !file_upload.is_uploaded) {
      handleUpload(file_upload, file_upload.file);
    } else {
      //pdfファイル以外でファイルが存在する場合

      const compress_image = new Image();
      compress_image.src = file_upload.preview_file_src;
      compress_image.onload = () => {
        if (
          compress_image.naturalWidth <= compressor_option.max_width &&
          compress_image.naturalHeight <= compressor_option.max_height
        ) {
          //アップロード済みの場合は書類確認,そうでない場合はファイルアップロードを行う
          if (file_upload.is_uploaded !== null && file_upload.is_uploaded) {
            handleConfirm(file_upload, file);
          } else {
            handleUpload(file_upload, file);
          }
        } else {
          //画像ファイルを圧縮する。縦幅か横幅のどちらかが定数指定の最大サイズを超えた場合は最大サイズに合わせて縮小する。
          new Compressor(file, {
            quality: compressor_option.quality, //可逆圧縮形式の画像出力時の画質
            maxHeight: compressor_option.max_height, //最大縦幅
            maxWidth: compressor_option.max_width, //最大サイズ
            mimeType: file.type, //出力ファイルのファイル形式
            success(compressor_image) {
              //アップロード済みの場合は書類確認,そうでない場合はファイルアップロードを行う
              if (file_upload.is_uploaded !== null && file_upload.is_uploaded) {
                handleConfirm(file_upload, compressor_image);
              } else {
                handleUpload(file_upload, compressor_image);
              }
            },

            //画像圧縮に失敗した場合はメッセージを表示
            error(error) {
              console.log(error);
              makeValidMessage(file_upload_error, file_upload, true);
            },
          });
        }
      };
    }
  };

  //読み込んだ画像を画面描画する。
  const fileRender = (file_upload: FileUploadType, file: Blob, file_name: string) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                //pdfの場合,代用のアイコンを表示
                preview_file_src:
                  file.type === 'application/pdf' ? pdf_icon : (reader.result as string),
                file_label: file_name,
                file: file,
                loading_msg: '',
                valid_messages: {},
                is_pdf: file.type === 'application/pdf',
              }
            : value
        )
      );
    };
  };

  /**
   * HEIF or HEICファイルをWeb利用可能な画像フォーマットに変換する
   * @param file_upload ファイルアップロード
   * @param file ファイル(Blob)
   */
  const convertHeicToWebImage = (file_upload: FileUploadType, file: Blob) => {
    const convert = async (file: Blob) => {
      try {
        // ローディング表示をする
        setFilesUpload((prev) =>
          prev.map((value) =>
            value.name === file_upload.name
              ? {
                  ...file_upload,
                  preview_file_src: '',
                  file_label: '',
                  file: new Blob(),
                  loading_msg: '画像を読み込んでいます。',
                  is_loading: true,
                  valid_messages: {},
                }
              : value
          )
        );

        // formDataを生成
        const fd = new FormData();
        fd.append('image', file);

        const headers = { 'content-type': 'multipart/form-data' };
        const response = await axios.post('/api/file/convert_heic', fd, { headers });

        // ファイルの読み込みに失敗した場合
        if (!response.data.result) {
          setFilesUpload((prev) =>
            prev.map((value) =>
              value.name === file_upload.name
                ? {
                    ...file_upload,
                    preview_file_src: '',
                    file_label: '',
                    file: new Blob(),
                    loading_msg: '',
                    is_loading: false,
                    valid_messages: {},
                  }
                : value
            )
          );
          throw 'ファイルの読み込みに失敗しました。';
        }

        // blobを生成
        const bin = atob(response.data.image);
        const buffer = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
          buffer[i] = bin.charCodeAt(i);
        }
        const blob = new Blob([buffer.buffer], {
          type: response.data.mime_type,
        });

        // 変換後の画像を画面に表示する
        setFilesUpload((prev) =>
          prev.map((value) =>
            value.name === file_upload.name
              ? {
                  ...file_upload,
                  preview_file_src: response.data.data_scheme + response.data.image,
                  file_label: response.data.original_name,
                  file: blob,
                  loading_msg: '',
                  is_loading: false,
                  valid_messages: {},
                }
              : value
          )
        );
      } catch (exception) {
        console.error(exception);
      }
    };
    convert(file);
  };

  /**
   * モーダルの表示制御
   * @param file_upload ファイルアップロード
   * @param modal_show_flg モーダル表示非表示フラグ
   */
  const modalControl = (file_upload: FileUploadType, modal_show_flg: boolean) => {
    setFilesUpload((prev) =>
      prev.map((value) =>
        value.name === file_upload.name
          ? {
              ...file_upload,
              is_modal_show: modal_show_flg,
            }
          : value
      )
    );
  };

  /**
   * 削除ボタン押下時のイベントハンドラ
   * @param file_upload ファイルアップロード
   */
  const handleCancel = async (file_upload: FileUploadType) => {
    try {
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                is_loading: true,
                loading_msg: '少々お待ちください...',
                is_modal_show: false,
              }
            : value
        )
      );

      const url = `/api/file/${file_upload.name}`;
      const request_param = {
        upload_token: upload_token,
        document_cd: file_upload.default_value,
        file_name: file_upload.file_upload_name,
      };
      const response = await axios.delete(url, {
        params: request_param,
      });

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                preview_file_src: '',
                file_label: '',
                is_loading: false,
                loading_msg: response.data.summary,
                is_uploaded: false,
                file_upload_date: '',
                file: new Blob(),
                is_modal_show: false,
                file_upload_name: '',
              }
            : value
        )
      );

      onCancel?.(file_upload.file_upload_name);
    } catch (error) {
      console.error(error);
    } finally {
      // 処理中フラグを無効にする。
      setIsProcessing(false);
    }
  };

  /**
   * 確認ボタン押下時のイベントハンドラ(暫定)
   * @param file_upload ファイルアップロード
   */
  const handleConfirmTmp = async (file_upload: FileUploadType) => {
    try {
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? { ...file_upload, is_loading: true, loading_msg: '少々お待ちください...' }
            : value
        )
      );
      const url = `/api/file/${file_upload.name}`;
      // ↓ POSTで送る
      const form = new FormData();
      const shelf_cd = file_upload.shelf_cd;
      form.append('file_path', file_upload.file_upload_name);
      form.append('document_cd', file_upload.default_value);
      form.append('ident_lv1_cd', ident_lv1_cd);
      form.append('ident_lv2_cd', ident_lv2_cd);
      form.append('ident_lv3_cd', ident_lv3_cd);
      form.append('reception_id', reception_id.toString());
      form.append('reception_cd', reception_cd);
      form.append('reception_sub_cd', reception_sub_cd);
      form.append('shelf_cd', shelf_cd);
      const response = await axios.post(url, form, {
        // ↓ headersを追記
        headers: {
          'X-HTTP-Method-Override': 'PUT',
        },
      });
      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                is_loading: false,
                confirm_date: response.data.confirm_date,
                confirm_user: response.data.confirm_user,
                loading_msg: response.data.summary,
              }
            : value
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      // 処理中フラグを無効にする。
      setIsProcessing(false);
    }
  };

  /**
   * 確認ボタン押下時のイベントハンドラ
   * @param file_upload ファイルアップロード
   */
  const handleConfirm = async (file_upload: FileUploadType, upload_image: Blob) => {
    try {
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? { ...file_upload, is_loading: true, loading_msg: '少々お待ちください...' }
            : value
        )
      );

      const edit_file_upload = editFileUpload(file_upload, files_upload, upload_image);

      const url = `/api/file/${edit_file_upload.name}`;

      // ↓ POSTで送る
      const response = await axios.post(url, createFormData(edit_file_upload), {
        // ↓ headersを追記
        headers: {
          'X-HTTP-Method-Override': 'PUT',
        },
      });
      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === edit_file_upload.name
            ? {
                ...edit_file_upload,
                is_loading: false,
                confirm_date: response.data.confirm_date,
                confirm_user: response.data.confirm_user,
                loading_msg: response.data.summary,
              }
            : value
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      // 処理中フラグを無効にする。
      setIsProcessing(false);
    }
  };

  /**
   * 確認取消ボタン押下時のイベントハンドラ
   * @param file_upload ファイルアップロード
   */
  const handleConfirmCancel = async (file_upload: FileUploadType) => {
    try {
      // 処理中フラグを有効にする。
      setIsProcessing(true);

      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? { ...file_upload, is_loading: true, loading_msg: '少々お待ちください...' }
            : value
        )
      );

      const url = `/api/file/${file_upload.name}/edit`;
      const request_param = {
        reception_cd: reception_cd,
        reception_sub_cd: reception_sub_cd,
        document_cd: file_upload.default_value,
        file_path: file_upload.file_upload_name,
      };
      const response = await axios.get(url, {
        params: request_param,
      });
      setFilesUpload((prev) =>
        prev.map((value) =>
          value.name === file_upload.name
            ? {
                ...file_upload,
                is_loading: false,
                confirm_date: '',
                confirm_user: '',
                loading_msg: response.data.summary,
              }
            : value
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      // 処理中フラグを有効にする。
      setIsProcessing(false);
    }
  };

  /**
   * マスキング完了処理
   *
   * @param {string} file_src データURL
   * @param {FileUploadType} file_upload
   * @returns void
   */
  const handleMaskingFinish = (file_src: string, file_upload: FileUploadType): void => {
    //取得したプレビュー画像を読み込み
    const new_image = new Image();
    new_image.src = file_src;

    //画像を読み込み後にcanvasタグを生成し,プレビュー画像を描画してblobを取得する。
    new_image.onload = () => {
      const canvas_image = document.createElement('canvas');
      canvas_image.width = new_image.naturalWidth;
      canvas_image.height = new_image.naturalHeight;
      const context = canvas_image.getContext('2d');

      if (context !== null) {
        context.imageSmoothingEnabled = false;
        context.drawImage(new_image, 0, 0);

        // 画像データを更新
        canvas_image.toBlob(function (blob) {
          if (blob !== null) {
            setFilesUpload((prev) =>
              prev.map((value) =>
                value.name === file_upload.name
                  ? {
                      ...file_upload,
                      preview_file_src: file_src,
                      file: blob,
                      is_loading: false,
                      loading_msg: '',
                    }
                  : value
              )
            );
          }
        });
      }
    };
  };

  /**
   * リクエストパラメータ(file_upload)の編集を行う
   * @param file_upload ファイルアップロード情報
   * @param fileｓ_upload ファイルアップロード情報の配列
   * @param fileｓ_upload アップロード用の画像ファイル
   * @return FileUploadType 編集済みのファイルアップロード情報
   */
  const editFileUpload = (
    file_upload: FileUploadType,
    files_upload: FileUploadType[],
    upload_image: Blob
  ) => {
    let result_file_upload = file_upload;
    //file_uploadにアップロード対象の圧縮済みまたはpdf形式の画像ファイルを設定する。
    files_upload.map((value) => {
      if (value.name === file_upload.name) {
        //file_uploadに圧縮済みの画像またはpdfファイルを設定する。
        result_file_upload = {
          ...value,
          file: upload_image,
        };
      }
    });

    return result_file_upload;
  };

  /**
   * クランプ文字を付与する
   * @param value 文字列
   * @param is_uploaded アップロード済みフラグ
   * @return string 処理後の文字列
   */
  const addClamp = (value: string, is_uploaded: boolean) => {
    if (
      value !== null &&
      typeof value !== 'undefined' &&
      is_uploaded &&
      value.length >= file_upload_clamp_config.length
    ) {
      const str = value.substring(0, file_upload_clamp_config.length);
      return `${str}${file_upload_clamp_config.clamp_string}`;
    }
    return value;
  };

  /**
   * ローディングを開始する
   * @param file_upload ファイルアップロード
   */
  const startLoading = (file_upload: FileUploadType, msg: string) => {
    setFilesUpload((prev) =>
      prev.map((value) =>
        value.name === file_upload.name
          ? {
              ...file_upload,
              is_loading: true,
              loading_msg: msg,
            }
          : value
      )
    );
  };

  /**
   * 「銀行へ提出」ボタンのdisabled判定
   *
   * @param {FileUploadType} file_upload ファイル用オブジェクト
   * @return {boolean} 判定結果(disabledならtrue)
   */
  const disabedSubmit = (file_upload: FileUploadType): boolean => {
    return (
      file_upload.is_loading ||
      file_upload.is_uploaded ||
      file_upload.default_value === '' ||
      file_upload.preview_file_src === ''
    );
  };

  /**
   * 「確認」「提出取消」ボタンのdisabled判定
   *
   * @param {FileUploadType} file_upload ファイル用オブジェクト
   * @return {boolean} 判定結果(disabledならtrue)
   */
  const disabedSubmitCancelAndConfirm = (file_upload: FileUploadType): boolean => {
    return file_upload.is_loading || !file_upload.is_uploaded;
  };

  /**
   * プレビュー画像周りに表示するボタンのdisabled判定
   *
   * @param {FileUploadType} file_upload ファイル用オブジェクト
   * @return {boolean} 判定結果(disabledならtrue)
   */
  const disabledPreview = (file_upload: FileUploadType): boolean => {
    return (
      (!is_staff && file_upload.is_uploaded) ||
      (is_staff && file_upload.confirm_date !== '') ||
      file_upload.is_loading ||
      file_upload.is_pdf
    );
  };

  /**
   * 「書類DL」ボタンのdisabled判定
   *
   * @param {FileUploadType} file_upload ファイル用オブジェクト
   * @return {boolean} 判定結果(disabledならtrue)
   */
  const disabedFileDownload = (file_upload: FileUploadType): boolean => {
    return file_upload.is_loading || !file_upload.is_uploaded || !is_staff;
  };

  /**
   * 対応するCDを持ったファイルアップローダのみ表示する
   */
  const filterByFmId = () => {
    return files_upload.filter((file) => file?.fm_id === fm_id);
  };

  /**
   * disabled===falseの時のview
   */
  const viewBlockEnabled = () => {
    return (
      <>
        {filterByFmId().map((file_upload, index) => (
          <div key={file_upload.name}>
            <RowTemplate
              label={file_upload.title}
              need={need}
              content={
                <>
                  <Row>
                    <Col md={6}>
                      <FileSelect
                        file_upload={file_upload}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleFileSelect(file_upload, event)
                        }
                      />
                    </Col>
                  </Row>
                  {/* プレビュー画像 */}
                  {file_upload.preview_file_src && (
                    <div>
                      <Canvas
                        img_src={file_upload.preview_file_src}
                        disabled={disabledPreview(file_upload)}
                        handleFinish={(file_src: string) => {
                          startLoading(file_upload, 'マスキング処理中');
                          handleMaskingFinish(file_src, file_upload);
                        }}
                        block_disabled={block_disabled}
                        file_download={file_upload.file_upload_name}
                        file_name={file_upload.file_label}
                        is_pdf={file_upload.is_pdf}
                      ></Canvas>
                    </div>
                  )}
                  <Row>
                    <Col md={6}>提出日時：{file_upload.file_upload_date}</Col>
                  </Row>
                  <Form.Group>
                    <InputGroup className="file-input-group">
                      <div className="file-name-box">
                        <Form.Control
                          id={`file-input-${file_upload.name}`}
                          className="d-none"
                          type="file"
                          accept="image/jpeg,image/png,application/pdf,image/gif,image/bmp,image/heic"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileInput(file_upload, event)
                          }
                          disabled={
                            file_upload.is_loading ||
                            file_upload.is_uploaded ||
                            file_upload.default_value === ''
                          }
                        />

                        <label
                          htmlFor={`file-input-${file_upload.name}`}
                          className={`form-control mb-0 ${
                            file_upload.file_label &&
                            file_upload.file_label.length > file_upload_clamp_config.length &&
                            !file_upload.is_uploaded
                              ? 'file-name-long'
                              : ''
                          }`}
                          style={{
                            cursor:
                              file_upload.is_loading ||
                              file_upload.is_uploaded ||
                              file_upload.default_value === ''
                                ? 'not-allowed'
                                : 'pointer',
                            backgroundColor:
                              file_upload.is_loading ||
                              file_upload.is_uploaded ||
                              file_upload.default_value === ''
                                ? '#e9ecef'
                                : '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file_upload.file_label ? file_upload.file_label : 'Choose file'}
                        </label>
                      </div>
                      <InputGroup>
                        <Button
                          className={
                            disabedSubmit(file_upload)
                              ? 'btn-shadow gradation'
                              : 'btn-positive gradation'
                          }
                          onClick={() => compressorImage(file_upload)}
                          disabled={disabedSubmit(file_upload)}
                        >
                          銀行へ提出
                        </Button>
                        <Button
                          className={
                            disabedSubmitCancelAndConfirm(file_upload)
                              ? 'btn-shadow gradation'
                              : 'btn-confirm-cancel gradation'
                          }
                          onClick={() => modalControl(file_upload, true)}
                          disabled={disabedSubmitCancelAndConfirm(file_upload)}
                        >
                          提出取消
                        </Button>
                        {/* 職員用ボタン */}
                        {is_staff && (
                          <>
                            <Button
                              className={
                                disabedSubmitCancelAndConfirm(file_upload)
                                  ? 'btn-shadow gradation'
                                  : 'btn-positive gradation'
                              }
                              onClick={() => compressorImage(file_upload)}
                              disabled={disabedSubmitCancelAndConfirm(file_upload)}
                            >
                              確認
                            </Button>
                            <Button
                              className="btn-shadow gradation"
                              onClick={() => handleConfirmCancel(file_upload)}
                              disabled={file_upload.is_loading || file_upload.confirm_date === ''}
                            >
                              確認取消
                            </Button>
                          </>
                        )}
                      </InputGroup>
                      <div className="ml-2"></div>
                      <div className="file-input-group">
                        {is_staff && (
                          <Button
                            className={
                              disabedFileDownload(file_upload)
                                ? 'round-btn btn-shadow gradation'
                                : 'round-btn btn-positive gradation'
                            }
                            onClick={() => handlefileDownLoad(file_upload.file_upload_name)}
                            disabled={disabedFileDownload(file_upload)}
                          >
                            書類DL
                          </Button>
                        )}
                        {file_upload.is_add && (
                          <Button
                            className="round-btn btn-shadow gradation"
                            onClick={() => handleAdd(file_upload)}
                            disabled={file_upload.is_loading || is_processing}
                          >
                            書類追加
                          </Button>
                        )}
                        {!file_upload.is_add && (
                          <Button
                            className="btn-shadow gradation"
                            onClick={() => handleDelete(file_upload)}
                            disabled={
                              file_upload.is_loading || file_upload.is_uploaded || is_processing
                            }
                          >
                            提出欄削除
                          </Button>
                        )}
                      </div>
                    </InputGroup>
                    {/* バリデーションメッセージ */}
                    <div>
                      {file_upload.valid_messages &&
                        Object.keys(file_upload.valid_messages).includes('file') && (
                          <ValidMessage message={file_upload.valid_messages.file} />
                        )}
                    </div>
                    {/* ローディング画像 */}
                    <div>
                      {file_upload.is_loading && (
                        <div className="spinner-border" role="status"></div>
                      )}
                      {file_upload.loading_msg && <span>{file_upload.loading_msg}</span>}
                    </div>
                  </Form.Group>
                </>
              }
            />
            <ModalTemplate
              key={file_upload.name}
              show={file_upload.is_modal_show}
              title="削除の確認"
              body={<div>提出済みのファイルを削除してもよろしいでしょうか</div>}
              ClickCallBackNo={() => {
                modalControl(file_upload, false);
              }}
              ClickCallBackYes={() => handleCancel(file_upload)}
            />
          </div>
        ))}
      </>
    );
  };

  /**
   * disabled===trueの時のview
   */
  const viewBlockDisabled = () => {
    return (
      <>
        {filterByFmId().map((file_upload) => (
          <div key={file_upload.name}>
            <RowTemplate
              label={file_upload.title}
              need={need}
              content={
                <>
                  <Row>
                    <Col md={4}>
                      {file_upload.source.map((src) => {
                        if (src.key === file_upload.default_value) {
                          return src.value;
                        }
                      })}
                    </Col>
                  </Row>
                  <Form.Group>
                    {/* プレビュー画像 */}
                    {file_upload.preview_file_src && (
                      <div>
                        <Canvas
                          img_src={file_upload.preview_file_src}
                          disabled={file_upload.is_uploaded}
                          handleFinish={(file_src: string) => {
                            handleMaskingFinish(file_src, file_upload);
                          }}
                          block_disabled={block_disabled}
                          file_download={file_upload.file_upload_name}
                          file_name={file_upload.file_label}
                          is_pdf={file_upload.is_pdf}
                        ></Canvas>
                      </div>
                    )}
                  </Form.Group>
                  <Row>
                    <Col md={6}>提出日時：{file_upload.file_upload_date}</Col>
                  </Row>
                  {is_staff && !disabedFileDownload(file_upload) && (
                    <Button
                      className={
                        disabedFileDownload(file_upload)
                          ? 'round-btn btn-shadow gradation'
                          : 'round-btn btn-positive gradation'
                      }
                      onClick={() => handlefileDownLoad(file_upload.file_upload_name)}
                      disabled={disabedFileDownload(file_upload)}
                    >
                      書類DL
                    </Button>
                  )}
                </>
              }
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      {!block_disabled ? viewBlockEnabled() : viewBlockDisabled()}
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </>
  );
};

export default memo(FileUpload);
